import React, {
    useCallback,
    useContext,
    useState,
    useEffect,
    memo,
    useMemo,
} from 'react';
import { withRouter } from 'react-router-dom';
import { withCookies } from 'react-cookie';
import { useMediaQuery } from 'react-responsive';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';

import '../../../css/components/batches/NavbarTrolley.css';

import { ReactComponent as TrolleyIcon } from '../../../images/icons/trolley.svg';
import TrolleyVehicleCard from './TrolleyVehicleCard';
import TrolleyHeader from './TrolleyHeader';
import TrolleyFooter from './TrolleyFooter';
import { makeGetFilteredCars } from '../../../redux/selectors/batches.js';
import NotificationBox from '../NotificationBox';
import Traductor from '../../Traductor';

import * as actions from '../../../redux/actions/batches';
import batchService from '../../../services/BatchesService';
import VehicleService from 'services/VehicleService';
import { UserContext } from '../../../contexts/UserContext';
import { getWSService } from '../../../js/WebSocketService';
import { checkCountDownStatus } from 'js/components/shared/vehicleFunctions';
import { isAppleDeviceOrNavegatorForDates } from 'Functions';
import useServerTime from 'hooks/useServerTime';

import Globals from '../../../Globals';
import { disableHTMLOverflow } from '../../../Functions';

import usePermissions from 'hooks/usePermissions';
import { READONLY_PERMISSION } from 'constants/Permissions';

const NavbarTrolley = ({
    cars,
    cookies,
    location,
    history,
    resetBatch,
    disableCarsFromBatch,
}) => {
    const { user } = useContext(UserContext);
    const { checkPermission } = usePermissions();
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const { serverTime, fetchServerTime } = useServerTime(true);
    const isMobile = useMediaQuery({ maxWidth: 600 });

    const t = useMemo(() => new Traductor(), []);
    const selectedBatchToAdd = location?.state?.selectedBatchToAdd;
    const disabled =
        checkPermission(READONLY_PERMISSION) ||
        (!selectedBatchToAdd && cars.availableCars.length < 3) ||
        cars.unavailableCars.length > 0;
    const bubbleAmount =
        cars.availableCars.length + cars.unavailableCars.length;
    const listHeight = window.innerHeight - (isMobile ? 48 : 68);

    const getCustomerId = useCallback(() => {
        if (user.buyerType === 1 && user.cifs.length === 1) {
            return user.cifs[0].Id.toString();
        }

        return user.preferredLocation && user.preferredLocation.ClientId
            ? user.preferredLocation.ClientId.toString()
            : null;
    }, [user.buyerType, user.cifs, user.preferredLocation]);

    const handleTrolleyClick = useCallback(
        e => {
            e.preventDefault();
            e.stopPropagation();

            setExpanded(!expanded);
        },
        [expanded]
    );

    const emitBatchEvent = useCallback(() => {
        const message = {
            // MessageType: "Batch",
            Vehicles: cars.availableCars.map(car => +car.WebSalesId),
        };
        const data = {
            type: 'batches',
            data: message,
        };

        getWSService().sendMessage('onMessage', data);
    }, [cars.availableCars]);

    const navigateToBatchDetails = useCallback(
        batch => {
            localStorage.setItem('selectedBatchDetails', JSON.stringify(batch));
            history.push({
                pathname: `${Globals.APPLICATION_PATH.BATCHES}/${batch.Id}`,
                state: {
                    batch,
                },
            });
        },
        [history]
    );

    const onAddVehiclesToSelectedBatch = useCallback(async () => {
        setLoading(true);
        const token = cookies.get(Globals.COOKIES.JWT).AccessToken;
        const data = {
            Vehicles: cars.availableCars.map(car => ({
                IdWebVehicles: car.WebSalesId,
                Amount: 0.0,
            })),
            IdBach: selectedBatchToAdd.details.Id,
        };
        const {
            body: {
                Result: { Result },
            },
        } = await batchService.addVehiclesToBach(data, token);

        setLoading(false);
        if (Result) {
            setExpanded(false);
            emitBatchEvent();
            navigateToBatchDetails({
                ...selectedBatchToAdd.details,
                NumVehicles:
                    cars.availableCars.length + selectedBatchToAdd?.cars.length,
            });
            resetBatch();
        }
    }, [
        cars.availableCars,
        cookies,
        emitBatchEvent,
        navigateToBatchDetails,
        resetBatch,
        selectedBatchToAdd,
    ]);

    //Validate persisted trolley cars to see that they are still available - > No bids/Not bought/Not added to a batch/Not finished auction
    const validateTrolley = useCallback(
        async token => {
            if (!token) return;

            const uncheckedCars = [];
            const { formattedCars, disabledCars } = cars.availableCars.reduce(
                (acc, car) => {
                    const status = checkCountDownStatus(
                        isAppleDeviceOrNavegatorForDates(car?.PublicationDate),
                        isAppleDeviceOrNavegatorForDates(
                            car?.PublicationEndDate
                        ),
                        serverTime
                    );
                    if (status !== 1) {
                        //Status 1 = upcoming, Status 1 = active, Status 2 = finished
                        return {
                            ...acc,
                            disabledCars: [...acc.disabledCars, car.WebSalesId],
                        };
                    } else {
                        return {
                            ...acc,
                            formattedCars: [
                                ...acc.formattedCars,
                                car.WebSalesId,
                            ],
                        };
                    }
                },
                {
                    formattedCars: [],
                    disabledCars: [],
                }
            );

            const responses = await Promise.all(
                formattedCars.map(carWebsalesId =>
                    new VehicleService().getVehicleStatus(token, carWebsalesId)
                )
            );

            responses.forEach((response, i) => {
                if (response) {
                    const parsedResponse = JSON.parse(response.body.Result);
                    if (parsedResponse) {
                        if (
                            !parsedResponse.HasOpenLotModality ||
                            parsedResponse.IsBlocked ||
                            parsedResponse.Status !== 2
                        ) {
                            disabledCars.push(formattedCars[i]);
                        } else {
                            uncheckedCars.push(formattedCars[i]);
                        }
                    }
                }
            });

            const bids = await new VehicleService().getVehicleBids(
                process.env.REACT_APP_AWS_GET_VEHICLE_BIDS,
                process.env.REACT_APP_AWS_BIDS_APIKEY_NAME,
                process.env.REACT_APP_AWS_BIDS_APIKEY_VALUE,
                uncheckedCars,
                token
            );

            if (bids && bids.length) {
                bids.forEach((response, i) => {
                    if (response.length) {
                        disabledCars.push(uncheckedCars[i]);
                    }
                });
            }

            disableCarsFromBatch(disabledCars);
            return disabledCars.length === 0;
        },
        [cars, disableCarsFromBatch, serverTime]
    );

    const onCreateBatch = useCallback(
        async ({ name }, { resetForm }) => {
            setLoading(true);
            const token = cookies.get(Globals.COOKIES.JWT)?.AccessToken;
            await fetchServerTime(); //refetch servertime for cars date validation
            const validTrolley = await validateTrolley(token);
            if (!validTrolley) {
                setLoading(false);
                return;
            }
            const data = {
                Vehicles: cars.availableCars.map(car => ({
                    IdWebVehicles: car.WebSalesId,
                    Amount: 0.0,
                })),
                IdCustomer: getCustomerId(),
                BuyMoment: '04',
                Name: name,
            };
            const {
                body: { Result },
            } = await batchService.createBatch(data, token);
            setLoading(false);
            if (Result?.IdBach) {
                const batch = {
                    Id: Result.IdBach,
                    Name: name,
                    NumVehicles: cars.availableCars.length,
                    Amount: 0,
                    IdStatus: '1',
                    CreateDate: Date.now(),
                    EndDate: cars.availableCars[0].PublicationEndDate,
                };
                setExpanded(false);
                emitBatchEvent();
                navigateToBatchDetails(batch);
                resetBatch();
                resetForm({});
            }
        },
        [
            cars.availableCars,
            cookies,
            emitBatchEvent,
            fetchServerTime,
            getCustomerId,
            navigateToBatchDetails,
            resetBatch,
            validateTrolley,
        ]
    );

    const disableEnterKey = useCallback(e => {
        if (
            e.keyIdentifier === 'U+000A' ||
            e.keyIdentifier === 'Enter' ||
            e.keyCode === 13
        ) {
            e.preventDefault();
            document.activeElement.blur();
        }
    }, []);

    useEffect(() => {
        if (expanded) {
            disableHTMLOverflow(true);
            window.addEventListener('keydown', disableEnterKey, true);
        } else {
            disableHTMLOverflow(false);
            window.removeEventListener('keydown', disableEnterKey, true);
        }
    }, [disableEnterKey, expanded]);

    const renderUnavailableCars = useCallback(
        () =>
            cars.unavailableCars.map(car => (
                <TrolleyVehicleCard key={car.Id} car={car} disabled />
            )),
        [cars.unavailableCars]
    );

    const renderAvailableCars = useCallback(
        () =>
            cars.availableCars.map((car, i) => (
                <TrolleyVehicleCard
                    key={car.Id}
                    car={car}
                    hideSeparator={i === cars.availableCars.length - 1}
                />
            )),
        [cars.availableCars]
    );

    const onDeleteCarFromSelectedBatch = useCallback(
        async ({ IdPublish, IdBach }) => {
            const token = cookies.get(Globals.COOKIES.JWT)?.AccessToken;

            const { selectedBatchToAdd } = location.state;

            if (selectedBatchToAdd.details.NumVehicles <= 3) {
                alert("Can't have less than 3 vehicles in batch");
                return;
            }

            const { Result } = await batchService.deleteBatchVehicle(
                {
                    IdBach,
                    IdPublishVehicle: IdPublish,
                },
                token
            );

            if (Result) {
                history.replace({
                    state: {
                        selectedBatchToAdd: {
                            ...selectedBatchToAdd,
                            cars: selectedBatchToAdd.cars.filter(
                                e => e.IdPublish !== IdPublish
                            ),
                        },
                    },
                });
            }
        },
        [cookies, history, location.state]
    );

    const renderSelectedBatch = useCallback(() => {
        if (selectedBatchToAdd) {
            return (
                <div>
                    <p className="u-h3 u-mv">
                        <span className="u-text-capitalize">
                            {t.getTraduction('Vehículos')}
                        </span>{' '}
                        <span className="u-text-lowercase">
                            {t.getTraduction('InYourBatch')}
                        </span>{' '}
                        ({selectedBatchToAdd.cars.length})
                    </p>
                    {selectedBatchToAdd.cars.map((car, i) => (
                        <TrolleyVehicleCard
                            key={car.IdVehicle}
                            car={car}
                            hideSeparator={
                                i === selectedBatchToAdd.cars.length - 1
                            }
                            onDelete={onDeleteCarFromSelectedBatch}
                        />
                    ))}
                </div>
            );
        }
    }, [selectedBatchToAdd, t, onDeleteCarFromSelectedBatch]);

    return (
        <li className="c-page-head-nav__item c-header-profile-dropdown js-dropdown js-is-ready">
            <a
                className="c-page-head-nav__link c-header-profile-dropdown__link"
                aria-expanded={expanded}
                href="#"
                style={{ position: 'relative', zIndex: 3 }}
                onClick={handleTrolleyClick}
            >
                <div
                    id="c-trolley-container"
                    className="c-header-profile-dropdown__link-container c-page-head-nav__link-text"
                >
                    <span
                        className="u-inline-item" //in order to display properly svg in firefox needs to be wrapped with a container that have height/and width set
                        style={{
                            width: '1.71429rem',
                            height: '1.71429rem',
                        }}
                    >
                        <TrolleyIcon
                            id="c-trolley-icon"
                            className="c-icon c-icon--small c-page-head-nav__link-icon"
                            alt="Shopping cart icon"
                            // width="1.71429rem"
                            // height="1.71429rem"
                        />
                    </span>
                    {bubbleAmount > 0 && (
                        <span
                            id="c-amount-bubble"
                            className="c-badge u-text-brand u-text-fs-3"
                        >
                            {bubbleAmount}
                        </span>
                    )}
                </div>
            </a>
            <div
                id="c-trolley-dropdown-container"
                className="c-page-head-nav__dropdown c-card"
                aria-hidden={!expanded}
            >
                <div
                    className="c-modal__overlay"
                    onClick={handleTrolleyClick}
                    style={{ pointerEvents: 'auto', zIndex: 2 }}
                />
                <ul
                    id="c-trolley-content-container"
                    className="c-card u-m-none u-p-none o-chop-apart"
                    style={{ zIndex: 3, height: listHeight, display: 'flex' }}
                >
                    <TrolleyHeader
                        amount={cars.availableCars.length}
                        onClose={handleTrolleyClick}
                        expanded={expanded}
                    />
                    <div
                        className={`u-pv-none u-mt-small ${
                            isMobile ? 'disable-scrollbar' : ''
                        }`}
                        style={{ overflowY: 'auto', flex: 1 }}
                        //webkit scroll bar media query for hiding in mobile
                    >
                        {renderUnavailableCars()}
                        {renderAvailableCars()}
                        {renderSelectedBatch()}
                    </div>
                    {disabled && (
                        <li className="c-page-head-nav__dropdown-list-item">
                            <NotificationBox
                                type="error"
                                color="alert"
                                message={
                                    cars.unavailableCars.length
                                        ? t.getTraduction(
                                              'UnavailableVehiclesInTrolley'
                                          )
                                        : t.getTraduction(
                                              'TrolleyLimitAmountError'
                                          )
                                }
                            />
                        </li>
                    )}
                    <hr className="u-p-none u-m-none u-mv" />
                    <TrolleyFooter
                        onSubmit={onCreateBatch}
                        disabled={disabled}
                        loading={loading}
                        onClose={handleTrolleyClick}
                        footer={
                            selectedBatchToAdd && {
                                disabled: !cars.availableCars.length,
                                label: selectedBatchToAdd.details.Name,
                                text: `${t.getTraduction(
                                    'NewVehiclesTotal'
                                )}: ${
                                    selectedBatchToAdd.cars.length +
                                    cars.availableCars.length
                                }`,
                                callback: onAddVehiclesToSelectedBatch,
                            }
                        }
                    />
                </ul>
            </div>
        </li>
    );
};

NavbarTrolley.propTypes = {
    cars: PropTypes.object,
    resetBatch: PropTypes.func.isRequired,
    disableCarsFromBatch: PropTypes.func.isRequired,
};

const makeMapStateToProps = () => {
    const getFilteredCars = makeGetFilteredCars();
    const mapStateToProps = (state, props) => {
        return {
            cars: getFilteredCars(state, props),
        };
    };
    return mapStateToProps;
};

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(actions, dispatch),
});

export default memo(
    connect(
        makeMapStateToProps,
        mapDispatchToProps
    )(withCookies(withRouter(NavbarTrolley)))
);
